import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@teals/ui-web";
import type { DataSource } from "../../store/api/dataSourcesApi";
import {
  DS_TYPE_LABELS,
  DS_TYPE_BADGE_VARIANT,
  BYO_PROVIDER_LABELS,
} from "./constants";
import { byoIndexName } from "./connectionConfig";
import { formatRelativeTime } from "./relativeTime";
import {
  DSCard,
  CardHeader,
  CardTitle,
  CardRecords,
  AgentChipRow,
  AgentChip,
  CardFooter,
  MetaItem,
  MetaItemGrow,
  DividerDot,
} from "./styles";

interface DataSourceCardProps {
  ds: DataSource;
  to: string;
  agentNames?: Record<string, string>;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({
  ds,
  to,
  agentNames = {},
}) => {
  const isByo = ds.dsType === "byo";

  const recordLabel =
    ds.recordCount != null && ds.recordCount > 0
      ? `${ds.recordCount.toLocaleString()} records`
      : "No records";

  const indexName = byoIndexName(ds.connectionConfig);
  const providerLabel = ds.provider
    ? (BYO_PROVIDER_LABELS[ds.provider] ?? ds.provider)
    : null;

  const agentChips = (ds.agentIds || []).map((id) => ({
    id,
    label: agentNames[id] || id,
  }));

  return (
    <Link to={to}>
      <DSCard padding={20} elevation="sm">
        <CardHeader>
          <div>
            <CardTitle>{ds.name}</CardTitle>
            <CardRecords>{recordLabel}</CardRecords>
          </div>
          <Badge variant={DS_TYPE_BADGE_VARIANT[ds.dsType] as any} size="sm">
            {DS_TYPE_LABELS[ds.dsType]}
          </Badge>
        </CardHeader>

        {agentChips.length > 0 && (
          <AgentChipRow>
            {agentChips.map((chip) => (
              <AgentChip key={chip.id}>{chip.label}</AgentChip>
            ))}
          </AgentChipRow>
        )}

        <CardFooter>
          {isByo && providerLabel ? (
            <MetaItemGrow
              title={
                indexName ? `${providerLabel} · ${indexName}` : providerLabel
              }
            >
              {providerLabel}
              {indexName ? ` · ${indexName}` : ""}
            </MetaItemGrow>
          ) : (
            <MetaItemGrow title={`Org: ${ds.orgName}`}>
              Org: {ds.orgName}
            </MetaItemGrow>
          )}
          <DividerDot />
          <MetaItem>{formatRelativeTime(ds.updatedAt)}</MetaItem>
          <DividerDot />
          <MetaItem>Open →</MetaItem>
        </CardFooter>
      </DSCard>
    </Link>
  );
};

export default DataSourceCard;
